import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import {
  DRAG_THRESHOLD_PX, MIN_DRAG_DURATION_MIN, SNAP_MINUTES, PIXELS_PER_MINUTE,
  DAY_MINUTES, pointerToMinute, minuteToDate, asDate,
} from '../components/calendar/weekGrid';
import { gridDrop, minuteAt, columnAt, minutesFromClock } from '../domain/calendarDrop';

// Dragging on the week grid — all three kinds.
//
// `create` is a new selection dragged on empty space; `resize` is dragging the
// bottom edge of an existing lesson; `move` is dragging the lesson itself to
// another day or time (#571). They share every mechanic except what happens
// on release, so they share a hook rather than duplicating the pointer
// bookkeeping three times.
//
// State is tracked in minutes, not pixels: the ghost can then re-render from
// the same numbers the commit uses, instead of two rounds of rect math that
// have to agree.
//
// The move is pointer events, like the other two, so a mouse, a pen and a
// finger all drive it: `touch-action: none` on the lesson's wrapper
// (WeekView.module.css) keeps a finger's drag from being taken for a scroll.
//
// The grid is the whole day (#749): every minute here is a minute of the day,
// a column's top is midnight, and a drop is held only inside the day
// (`gridDrop`'s own bounds).

// Pointer capture keeps the gesture on the column even if the cursor leaves it.
// It is an optimisation, not a requirement — the listeners are on the window —
// and it throws NotFoundError if the pointer is no longer active, which a
// synthetic or already-released pointer will be. Losing capture is survivable;
// an exception out of a pointerdown handler is not.
function capturePointer(el, pointerId) {
  try {
    el?.setPointerCapture?.(pointerId);
  } catch {
    // Gesture still works; it just is not captured.
  }
}

// How long after a drag a click is taken as that drag's own release. The
// browser follows a pointerup with a click on whatever the press and the
// release had in common — the card itself, when a lesson is nudged by less
// than its own height — and that click would open the popover on top of the
// move. A timestamp rather than a flag cleared on the next press, because a
// keyboard Enter on a card is a click with no press before it.
const CLICK_AFTER_DRAG_MS = 400;

export default function useLessonDrag({
  days, dayColRefs, onSlotClick, onResizeLesson, onMoveLesson, isSameDay,
}) {
  const [drag, setDrag] = useState(null);
  // The gesture as of the last event, for the release to read. The release
  // calls back into the page, and doing that from inside a state updater would
  // run it twice wherever React replays an updater.
  const dragRef = useRef(null);
  const clickSuppressedUntil = useRef(0);
  const update = (next) => {
    const value = typeof next === 'function' ? next(dragRef.current) : next;
    dragRef.current = value;
    setDrag(value);
  };

  const startCreate = (dayIndex, day, e) => {
    // A press that lands on a lesson belongs to the lesson, not the column;
    // otherwise releasing it would also open a "New Calendar Event" drawer
    // beside the lesson's popover. `data-lesson-card`, which LessonCard puts on
    // its root, rather than a class name, which a stylesheet may rename;
    // test/ui/lessonClickOnly.test.jsx holds the two files to it.
    if (e.target.closest('[data-lesson-card]')) return;
    if (e.button !== undefined && e.button !== 0) return;
    const colEl = dayColRefs.current[dayIndex];
    if (!colEl) return;
    const startMinute = pointerToMinute(e.clientY, colEl, e.shiftKey);
    update({
      mode: 'create',
      dayIndex,
      day,
      startMinute,
      currentMinute: startMinute,
      anchorClientY: e.clientY,
      committed: false,
    });
    capturePointer(colEl, e.pointerId);
  };

  const startResize = (lesson, e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const lessonDate = asDate(lesson.date);
    const dayIndex = days.findIndex((d) => isSameDay(d, lessonDate));
    if (dayIndex < 0) return;
    const startMinute = lessonDate.getHours() * 60 + lessonDate.getMinutes();
    update({
      mode: 'resize',
      dayIndex,
      day: days[dayIndex],
      lesson,
      startMinute,
      currentMinute: startMinute + (lesson.durationMin || 60),
      anchorClientY: e.clientY,
      committed: false,
    });
    capturePointer(dayColRefs.current[dayIndex], e.pointerId);
  };

  // Picking a lesson up by its body (#571). Nothing moves until the pointer
  // has travelled past the threshold, so a press and release in place is
  // still the click that opens the popover — test/ui/lessonClickOnly.test.jsx.
  //
  // Not captured: the target column is whichever one the pointer is over,
  // found from the columns' rectangles on each move, and the listeners are on
  // the window either way.
  //
  // An all-day event is not offered: it lives in the band above the clock,
  // with no time for a drop to give it.
  const startMove = (lesson, e) => {
    if (!onMoveLesson || lesson.allDay) return;
    if (e.button !== undefined && e.button !== 0) return;
    const lessonDate = asDate(lesson.date);
    const dayIndex = days.findIndex((d) => isSameDay(d, lessonDate));
    if (dayIndex < 0) return;
    const colEl = dayColRefs.current[dayIndex];
    if (!colEl) return;
    const startMinute = lessonDate.getHours() * 60 + lessonDate.getMinutes();
    const rect = colEl.getBoundingClientRect();
    const pointerMinute = minuteAt(e.clientY - rect.top, PIXELS_PER_MINUTE);
    update({
      mode: 'move',
      dayIndex,
      day: days[dayIndex],
      lesson,
      // How far into the lesson it was picked up, so it is let go by the same
      // point rather than jumping to put its top edge under the pointer.
      grabMinute: pointerMinute - startMinute,
      target: null,
      targetIndex: dayIndex,
      anchorClientX: e.clientX,
      anchorClientY: e.clientY,
      committed: false,
    });
  };

  // Where a move would land if it were let go here: the column under the
  // pointer and the five-minute step under it, by the domain's rule.
  const moveTarget = (d, ev) => {
    const rects = days.map((_, i) => dayColRefs.current[i]?.getBoundingClientRect() || null);
    const column = columnAt(ev.clientX, rects);
    const rect = rects[column];
    if (column < 0 || !rect) return { targetIndex: d.targetIndex, target: d.target };
    const target = gridDrop({
      firstDate: format(days[0], 'yyyy-MM-dd'),
      column,
      pointerMinute: minuteAt(ev.clientY - rect.top, PIXELS_PER_MINUTE),
      grabMinute: d.grabMinute,
      durationMin: d.lesson.durationMin || 60,
      gridStart: 0,
      gridEnd: DAY_MINUTES,
    });
    return { targetIndex: column, target };
  };

  useEffect(() => {
    if (!drag) return undefined;
    const colEl = dayColRefs.current[drag.dayIndex];
    if (!colEl) return undefined;

    const onMove = (ev) => {
      update((d) => {
        if (!d) return d;
        if (d.mode === 'move') {
          const travelled = Math.hypot(ev.clientX - d.anchorClientX, ev.clientY - d.anchorClientY);
          return {
            ...d,
            ...moveTarget(d, ev),
            committed: d.committed || travelled > DRAG_THRESHOLD_PX,
          };
        }
        const min = pointerToMinute(ev.clientY, colEl, ev.shiftKey);
        // Committed once, committed for the rest of the gesture: letting it
        // flip back on a small return movement would cancel a drag the user is
        // still making.
        const committed = d.committed || Math.abs(ev.clientY - d.anchorClientY) > DRAG_THRESHOLD_PX;
        return { ...d, currentMinute: min, committed };
      });
    };

    const commit = () => {
      const d = dragRef.current;
      update(null);
      if (!d) return;
      if (d.committed) clickSuppressedUntil.current = Date.now() + CLICK_AFTER_DRAG_MS;
      if (d.mode === 'create') {
        const startMin = Math.min(d.startMinute, d.currentMinute);
        const endMin = Math.max(d.startMinute, d.currentMinute);
        if (!d.committed) {
          // Never passed the threshold, so this was a click. One on the very
          // bottom edge is the last quarter-hour, not the next midnight.
          onSlotClick?.(minuteToDate(d.day, Math.min(d.startMinute, DAY_MINUTES - SNAP_MINUTES)));
        } else if (endMin - startMin >= MIN_DRAG_DURATION_MIN) {
          onSlotClick?.(minuteToDate(d.day, startMin), endMin - startMin);
        }
      } else if (d.mode === 'resize' && d.committed) {
        const duration = Math.max(SNAP_MINUTES, d.currentMinute - d.startMinute);
        if (duration !== d.lesson.durationMin) onResizeLesson?.(d.lesson, duration);
      } else if (d.mode === 'move' && d.committed && d.target) {
        // Handed on even when it lands where it started: `reschedulePatch` is
        // the rule for "nothing moved", and it writes nothing.
        onMoveLesson?.(d.lesson, d.target);
      }
    };

    const cancel = () => update(null);
    const onKeyDown = (ev) => { if (ev.key === 'Escape') cancel(); };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', commit);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', commit);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', onKeyDown);
    };
    // Only the column matters for the ref lookup; depending on currentMinute
    // would tear down and rebind the listeners on every pointer move. A move's
    // target is read from the refs on each event, so it needs nothing here.
  }, [drag?.dayIndex, Boolean(drag), onSlotClick, onResizeLesson, onMoveLesson]);

  // Whether a click arriving now is a drag's own release rather than a click.
  const clickSuppressed = () => Date.now() < clickSuppressedUntil.current;

  // What the ghost should look like on a given column, or null when there is
  // nothing to draw. Keeps the geometry decision out of the JSX.
  const ghostFor = (dayIndex) => {
    if (drag?.mode === 'move') {
      if (!drag.committed || !drag.target || drag.targetIndex !== dayIndex) return null;
      const startMin = minutesFromClock(drag.target.time);
      const duration = drag.lesson.durationMin || 60;
      return { startMin, endMin: startMin + duration, duration, moving: drag.lesson };
    }
    if (!drag || drag.dayIndex !== dayIndex || drag.mode !== 'create' || !drag.committed) return null;
    const startMin = Math.min(drag.startMinute, drag.currentMinute);
    const endMin = Math.max(drag.startMinute, drag.currentMinute);
    const duration = endMin - startMin;
    if (duration < MIN_DRAG_DURATION_MIN) return null;
    return { startMin, endMin, duration };
  };

  // The duration to draw a lesson at right now — the live one while it is being
  // resized, its stored one otherwise.
  const liveDurationFor = (lesson) => {
    const resizing = drag?.mode === 'resize' && drag.lesson?.id === lesson.id;
    if (!resizing) return { duration: lesson.durationMin || 60, resizing: false };
    return {
      duration: Math.max(SNAP_MINUTES, drag.currentMinute - drag.startMinute),
      resizing: drag.committed,
    };
  };

  // Whether a lesson is the one being carried, so its card can step back
  // while the ghost shows where it would land.
  const isMoving = (lesson) => drag?.mode === 'move' && drag.committed && drag.lesson?.id === lesson.id;

  return {
    drag, startCreate, startResize, startMove, ghostFor, liveDurationFor, isMoving, clickSuppressed,
  };
}
